import { useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { FiPackage, FiShoppingBag, FiHome } from 'react-icons/fi';
import { motion } from 'framer-motion';
import './OrderSuccessPage.css';

export default function OrderSuccessPage() {
  const { orderId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  // If someone lands here without order state, redirect
  useEffect(() => {
    if (!orderId) navigate('/', { replace: true });
  }, [orderId, navigate]);

  const orderNumber = state?.orderNumber || `#${orderId?.slice(-8).toUpperCase()}`;
  const total = state?.total;
  const itemCount = state?.itemCount;

  return (
    <div className="success-page">
      <div className="success-card">
        {/* Animated checkmark */}
        <motion.div
          className="check-wrap"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
        >
          <svg className="check-svg" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle className="check-circle" cx="40" cy="40" r="37" stroke="#22c55e" strokeWidth="4" />
            <path className="check-mark" d="M22 41 L34 53 L58 29" stroke="#22c55e" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="success-content"
        >
          <h1 className="success-title">Order Placed!</h1>
          <p className="success-sub">
            Thank you for shopping with <strong>Fivora</strong>. Your order has been confirmed.
          </p>

          {/* Order info card */}
          <div className="order-info-card">
            <div className="order-info-row">
              <span className="order-info-label">Order Number</span>
              <span className="order-info-value order-number">{orderNumber}</span>
            </div>
            {total !== undefined && (
              <div className="order-info-row">
                <span className="order-info-label">Amount Paid</span>
                <span className="order-info-value">Rs. {Number(total).toFixed(2)}</span>
              </div>
            )}
            {itemCount !== undefined && (
              <div className="order-info-row">
                <span className="order-info-label">Items</span>
                <span className="order-info-value">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
              </div>
            )}
            <div className="order-info-row">
              <span className="order-info-label">Status</span>
              <span className="badge badge-success">Order Placed</span>
            </div>
          </div>

          <p className="delivery-est">
            🚚 Estimated delivery: <strong>3–5 business days</strong>
          </p>

          {/* Actions */}
          <div className="success-actions">
            <Link to="/orders" className="btn btn-primary btn-lg">
              <FiPackage size={17} /> Track Order
            </Link>
            <Link to="/products" className="btn btn-ghost btn-lg">
              <FiShoppingBag size={17} /> Continue Shopping
            </Link>
            <Link to="/" className="btn btn-ghost">
              <FiHome size={15} /> Home
            </Link>
          </div>

          {/* Confetti dots decoration */}
          <div className="confetti-wrap" aria-hidden="true">
            {['#6C47FF','#FF6B35','#00D4AA','#f59e0b','#ec4899'].map((color, i) => (
              <motion.span
                key={i}
                className="confetti-dot"
                style={{ background: color }}
                initial={{ y: 0, opacity: 1 }}
                animate={{ y: -60 - i * 15, opacity: 0, x: (i % 2 === 0 ? 1 : -1) * (i + 1) * 12 }}
                transition={{ delay: 0.6 + i * 0.08, duration: 0.9, ease: 'easeOut' }}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
